import { useTranslation } from 'react-i18next'
import { Card } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { MoreVertical, Play, Pencil, Copy, Trash2, History } from 'lucide-react'
import type { Agent } from '@/schemas/agent'

interface AgentCardProps {
  agent: Agent
  onClick?: () => void
  onEdit?: () => void
  onDuplicate?: () => void
  onDelete?: () => void
  onTest?: () => void
  onHistory?: () => void
}

// Map agent name keywords to emojis
const EMOJI_KEYWORD_MAP: Array<[string[], string]> = [
  [['venda', 'sales'], '🛒'],
  [['dado', 'data', 'analis'], '📊'],
  [['suporte', 'support'], '🔧'],
  [['content', 'write', 'escrit'], '✍️'],
  [['code', 'dev', 'program'], '👨‍💻'],
  [['meeting', 'reuniao', 'agenda'], '📅'],
  [['research', 'pesquisa'], '🔍'],
  [['email', 'mail'], '📧'],
  [['chat', 'conversa'], '💬'],
]

function getAgentEmoji(name: string): string {
  const lowerName = name.toLowerCase()
  for (const [keywords, emoji] of EMOJI_KEYWORD_MAP) {
    if (keywords.some((keyword) => lowerName.includes(keyword))) {
      return emoji
    }
  }
  return '🤖'
}

// Get LLM model display name
function getModelDisplayName(
  llmConfig: Agent['llm_config'],
  t: (key: string) => string
): string {
  if (llmConfig?.model) {
    return llmConfig.model
  }
  if (llmConfig?.provider) {
    return llmConfig.provider
  }
  return t('agents:modelNotConfigured')
}

// Status badge configuration (without labels - will use i18n)
const STATUS_CONFIG = {
  active: {
    bg: 'bg-emerald-500/20',
    text: 'text-emerald-400',
  },
  inactive: {
    bg: 'bg-amber-500/20',
    text: 'text-amber-400',
  },
} as const

export function AgentCard({
  agent,
  onClick,
  onEdit,
  onDuplicate,
  onDelete,
  onTest,
  onHistory,
}: AgentCardProps) {
  const { t } = useTranslation('agents')
  const status = agent.is_active ? 'active' : 'inactive'
  const statusConfig = STATUS_CONFIG[status]
  const emoji = getAgentEmoji(agent.name)
  const modelName = getModelDisplayName(agent.llm_config, t)

  return (
    <Card
      variant="glass"
      className="p-6 cursor-pointer group hover:border-indigo-500/30 transition-all duration-200"
      onClick={onClick}
    >
      {/* Header: Avatar + Status + Menu */}
      <div className="flex items-start justify-between mb-4">
        {/* Avatar */}
        <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-indigo-500/20 to-purple-500/20 flex items-center justify-center text-2xl">
          {emoji}
        </div>

        {/* Status + Menu */}
        <div className="flex items-center gap-2">
          {/* Status Badge */}
          <span
            className={`px-2 py-1 text-xs font-medium rounded-full ${statusConfig.bg} ${statusConfig.text}`}
          >
            {t(`card.${status}`)}
          </span>

          {/* Menu (visible on hover) */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8 opacity-0 group-hover:opacity-100 transition-opacity"
                onClick={(e) => e.stopPropagation()}
              >
                <MoreVertical className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="glass border-gray-700/50">
              {onEdit && (
                <DropdownMenuItem
                  onClick={(e) => {
                    e.stopPropagation()
                    onEdit()
                  }}
                  className="focus:bg-white/5"
                >
                  <Pencil className="mr-2 h-4 w-4" />
                  {t('card.edit')}
                </DropdownMenuItem>
              )}
              {onDuplicate && (
                <DropdownMenuItem
                  onClick={(e) => {
                    e.stopPropagation()
                    onDuplicate()
                  }}
                  className="focus:bg-white/5"
                >
                  <Copy className="mr-2 h-4 w-4" />
                  {t('card.duplicate')}
                </DropdownMenuItem>
              )}
              {onHistory && (
                <DropdownMenuItem
                  onClick={(e) => {
                    e.stopPropagation()
                    onHistory()
                  }}
                  className="focus:bg-white/5"
                >
                  <History className="mr-2 h-4 w-4" />
                  {t('card.history')}
                </DropdownMenuItem>
              )}
              {onDelete && (
                <DropdownMenuItem
                  onClick={(e) => {
                    e.stopPropagation()
                    onDelete()
                  }}
                  className="text-red-400 focus:text-red-400 focus:bg-red-500/10"
                >
                  <Trash2 className="mr-2 h-4 w-4" />
                  {t('card.delete')}
                </DropdownMenuItem>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      {/* Title + Model */}
      <div className="mb-4">
        <h3 className="text-lg font-semibold text-gray-100 truncate mb-1">
          {agent.name}
        </h3>
        <p className="text-sm text-gray-500 truncate">{modelName}</p>
      </div>

      {/* Description (if exists) */}
      {agent.description && (
        <p className="text-sm text-gray-400 line-clamp-2 mb-4">
          {agent.description}
        </p>
      )}

      {/* Footer: Test button (visible on hover) */}
      {onTest && (
        <div className="flex justify-end opacity-0 group-hover:opacity-100 transition-opacity">
          <Button
            variant="ghost"
            size="sm"
            className="gap-2 text-indigo-400 hover:text-indigo-300 hover:bg-indigo-500/10"
            onClick={(e) => {
              e.stopPropagation()
              onTest()
            }}
          >
            <Play className="h-4 w-4" />
            {t('card.test')}
          </Button>
        </div>
      )}
    </Card>
  )
}

// Card for creating new agent
export function NewAgentCard({ onClick }: { onClick?: () => void }) {
  const { t } = useTranslation('agents')

  return (
    <Card
      variant="outlined"
      className="p-6 cursor-pointer flex flex-col items-center justify-center min-h-[200px] hover:border-indigo-500/50 hover:bg-indigo-500/5 transition-all duration-200"
      onClick={onClick}
    >
      <div className="w-14 h-14 rounded-xl bg-gray-700/30 flex items-center justify-center text-gray-500 mb-4">
        <span className="text-3xl">+</span>
      </div>
      <p className="text-gray-400 font-medium">{t('newAgentCard')}</p>
    </Card>
  )
}
